import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import React__default, { useMemo, useState, useEffect, useLayoutEffect } from "react";
import { useIsCompactVoiceLayout } from "../../hooks/useIsCompactVoiceLayout.js";
import { AmazonStarRating } from "./AmazonStarRating.js";
const WIDGET_KEYFRAMES = `
@keyframes voice-widget-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;
const THUMB_COUNT = 5;
const FG = "var(--voice-widget-fg, #2B4168)";
const MUTED = "var(--voice-widget-muted, #6B7280)";
const BORDER = "var(--voice-widget-border, #E5E7EB)";
const SURFACE = "var(--voice-widget-card-bg, #FFFFFF)";
const BUTTON_BG = "var(--voice-widget-button-bg, #2B4168)";
const BUTTON_FG = "var(--voice-widget-button-fg, #FFFFFF)";
const HAIRLINE = "var(--voice-widget-hairline, #F1F1F4)";
const KEY_FEATURES_COLLAPSED_COUNT = 3;
const KEY_FEATURES_EXPAND_MS = 260;
function ImagePlaceholder({ size }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: FG,
      strokeWidth: 1.5,
      style: { opacity: 0.3, display: "block" },
      "aria-hidden": true,
      children: [
        /* @__PURE__ */ jsx("rect", { x: "3", y: "3", width: "18", height: "18", rx: "2" }),
        /* @__PURE__ */ jsx("circle", { cx: "9", cy: "9", r: "2" }),
        /* @__PURE__ */ jsx("path", { d: "m21 15-5-5L5 21" })
      ]
    }
  );
}
function ImageWell({ src, alt, height, padding }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);
  const showImg = !!src && !broken;
  return /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding,
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden"
      },
      children: showImg ? /* @__PURE__ */ jsx(
        "img",
        {
          src,
          alt,
          onError: () => setBroken(true),
          style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }
        }
      ) : /* @__PURE__ */ jsx(ImagePlaceholder, { size: 48 })
    }
  );
}
function Thumb({
  src,
  active,
  onClick,
  index
}) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);
  const interactive = !!src && !!onClick;
  const showImg = !!src && !broken;
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: interactive ? onClick : void 0,
      "aria-label": `View ${index + 1}`,
      style: {
        flex: 1,
        aspectRatio: "1 / 1",
        background: SURFACE,
        border: `1.5px solid ${active ? FG : BORDER}`,
        borderRadius: 8,
        padding: 6,
        cursor: interactive ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        transition: "border-color 160ms ease-out",
        font: "inherit",
        color: "inherit"
      },
      children: showImg ? /* @__PURE__ */ jsx(
        "img",
        {
          src,
          alt: "",
          onError: () => setBroken(true),
          style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }
        }
      ) : /* @__PURE__ */ jsx(ImagePlaceholder, { size: 20 })
    }
  );
}
function useImageGallery(data) {
  const ordered = useMemo(() => {
    const out = [];
    const seen = /* @__PURE__ */ new Set();
    const push = (u) => {
      if (!u || typeof u !== "string") return;
      if (seen.has(u)) return;
      seen.add(u);
      out.push(u);
    };
    push(data.imageUrl);
    for (const u of data.imageUrls ?? []) push(u);
    return out;
  }, [data.imageUrl, data.imageUrls]);
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    setActiveIndex(0);
  }, [ordered]);
  const thumbs = Array.from({ length: THUMB_COUNT }, (_, i) => ordered[i]);
  const mainSrc = ordered[Math.min(activeIndex, Math.max(0, ordered.length - 1))] ?? ordered[0];
  return { mainSrc, thumbs, activeIndex, setActiveIndex };
}
function CategoryBreadcrumb({
  category,
  subcategory,
  compact
}) {
  const parts = compact ? [subcategory || category] : [category, subcategory];
  const label = parts.filter((p) => typeof p === "string" && p.trim().length > 0).join(" · ");
  if (!label) return null;
  return /* @__PURE__ */ jsx("div", { style: { fontSize: 13, color: FG, opacity: 0.75, marginBottom: 14 }, children: label });
}
function FeatureBullet({ text }) {
  return /* @__PURE__ */ jsxs(
    "li",
    {
      style: {
        fontSize: 13,
        lineHeight: 1.5,
        color: FG,
        padding: "6px 0 6px 22px",
        position: "relative"
      },
      children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            style: {
              position: "absolute",
              left: 0,
              top: 6,
              color: FG,
              fontWeight: 700,
              fontSize: 13
            },
            children: "✓"
          }
        ),
        text
      ]
    }
  );
}
function KeyFeatures({
  bullets,
  collapseKeyFeatures
}) {
  const [expanded, setExpanded] = useState(false);
  const extraRef = React__default.useRef(null);
  const [extraMaxHeight, setExtraMaxHeight] = useState(0);
  const bulletsSignature = bullets.join("");
  const canCollapse = collapseKeyFeatures && bullets.length > KEY_FEATURES_COLLAPSED_COUNT;
  const extraBullets = canCollapse ? bullets.slice(KEY_FEATURES_COLLAPSED_COUNT) : [];
  useLayoutEffect(() => {
    if (!canCollapse) {
      setExtraMaxHeight(0);
      return;
    }
    const el = extraRef.current;
    if (!el) return;
    setExtraMaxHeight(expanded ? el.scrollHeight : 0);
  }, [canCollapse, expanded, bulletsSignature]);
  if (bullets.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 12 }, children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          fontSize: 12,
          fontWeight: 600,
          color: FG,
          opacity: 0.7,
          textTransform: "uppercase",
          letterSpacing: "0.05em"
        },
        children: "Key Features"
      }
    ),
    /* @__PURE__ */ jsx("ul", { style: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 0 }, children: (canCollapse ? bullets.slice(0, KEY_FEATURES_COLLAPSED_COUNT) : bullets).map((b, i) => /* @__PURE__ */ jsx(FeatureBullet, { text: b }, i)) }),
    canCollapse ? /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          overflow: "hidden",
          maxHeight: expanded ? extraMaxHeight : 0,
          opacity: expanded ? 1 : 0,
          transition: `max-height ${KEY_FEATURES_EXPAND_MS}ms ease-out, opacity ${KEY_FEATURES_EXPAND_MS}ms ease-out`
        },
        "aria-hidden": !expanded,
        children: /* @__PURE__ */ jsx(
          "ul",
          {
            ref: extraRef,
            style: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 0 },
            children: extraBullets.map((b, i) => /* @__PURE__ */ jsx(FeatureBullet, { text: b }, i))
          }
        )
      }
    ) : null,
    canCollapse ? /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => setExpanded((v) => !v),
        "aria-expanded": expanded,
        style: {
          alignSelf: "flex-start",
          margin: 0,
          padding: 0,
          border: "none",
          background: "none",
          font: "inherit",
          fontSize: 13,
          fontWeight: 500,
          color: FG,
          cursor: "pointer",
          textDecoration: "underline",
          textUnderlineOffset: 3
        },
        children: expanded ? "See less" : "See more"
      }
    ) : null
  ] });
}
function ViewProductPill({ price, href }) {
  const sharedStyle = {
    background: BUTTON_BG,
    color: BUTTON_FG,
    border: "none",
    padding: "14px 28px",
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 999,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    cursor: href ? "pointer" : "default",
    width: "100%",
    maxWidth: 400,
    boxSizing: "border-box"
  };
  const inner = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("span", { children: "View product" }),
    price ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": true,
          style: {
            width: 4,
            height: 4,
            borderRadius: "50%",
            background: BUTTON_FG,
            opacity: 0.85,
            display: "inline-block",
            flexShrink: 0
          }
        }
      ),
      /* @__PURE__ */ jsx("span", { style: { fontWeight: 600 }, children: price })
    ] }) : null
  ] });
  if (href) {
    return /* @__PURE__ */ jsx("a", { href, target: "_blank", rel: "noreferrer", style: sharedStyle, children: inner });
  }
  return /* @__PURE__ */ jsx("div", { style: sharedStyle, children: inner });
}
const ProductCardWidget = ({
  data,
  collapseKeyFeatures = false
}) => {
  const isCompact = useIsCompactVoiceLayout();
  const href = data.retailerProductPageUrl;
  const { mainSrc, thumbs, activeIndex, setActiveIndex } = useImageGallery(data);
  const bullets = useMemo(
    () => (data.featureBullets ?? []).map((b) => typeof b === "string" ? b.trim() : "").filter((b) => b.length > 0).slice(0, 8),
    [data.featureBullets]
  );
  const Brand = data.brand ? /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        fontSize: 12,
        fontWeight: 600,
        color: FG,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: 8
      },
      children: data.brand
    }
  ) : null;
  const Title = /* @__PURE__ */ jsx(
    "h2",
    {
      style: {
        fontSize: isCompact ? 18 : 20,
        fontWeight: 600,
        lineHeight: 1.3,
        color: FG,
        margin: "0 0 8px"
      },
      children: data.title
    }
  );
  const Rating = /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 13,
        color: MUTED,
        marginBottom: 20,
        paddingBottom: 20,
        borderBottom: `1px solid ${HAIRLINE}`
      },
      children: /* @__PURE__ */ jsx(
        AmazonStarRating,
        {
          amazonRating: data.amazonRating,
          amazonReviewCount: data.amazonReviewCount,
          size: 14,
          countFontSize: 13,
          format: "long"
        }
      )
    }
  );
  const Gallery = /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 12 }, children: [
    /* @__PURE__ */ jsx(ImageWell, { src: mainSrc, alt: data.title, height: isCompact ? 240 : 320, padding: isCompact ? 20 : 24 }),
    /* @__PURE__ */ jsx("div", { style: { display: "flex", gap: 8 }, children: thumbs.map((src, i) => /* @__PURE__ */ jsx(
      Thumb,
      {
        src,
        index: i,
        active: i === activeIndex,
        onClick: src ? () => setActiveIndex(i) : void 0
      },
      i
    )) })
  ] });
  const Details = /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", flex: 1 }, children: [
    Brand,
    Title,
    /* @__PURE__ */ jsx(CategoryBreadcrumb, { category: data.category, subcategory: data.subcategory, compact: isCompact }),
    Rating,
    /* @__PURE__ */ jsx(
      KeyFeatures,
      {
        bullets,
        collapseKeyFeatures
      },
      data.stacklineSku
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          marginTop: isCompact ? 20 : "auto",
          paddingTop: isCompact ? 16 : 24,
          borderTop: isCompact ? `1px solid ${HAIRLINE}` : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start"
        },
        children: /* @__PURE__ */ jsx(ViewProductPill, { price: data.price, href })
      }
    )
  ] });
  return /* @__PURE__ */ jsxs("div", { style: { width: "100%", display: "flex", justifyContent: "center", boxSizing: "border-box" }, children: [
    /* @__PURE__ */ jsx("style", { children: WIDGET_KEYFRAMES }),
    /* @__PURE__ */ jsxs(
      "article",
      {
        style: {
          width: "100%",
          maxWidth: isCompact ? 420 : 1024,
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: 12,
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr",
          minHeight: isCompact ? void 0 : 520,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          animation: "voice-widget-fade-in 0.28s ease-out both",
          boxSizing: "border-box"
        },
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                padding: isCompact ? "20px 20px 0" : 32,
                background: SURFACE,
                borderRight: isCompact ? "none" : `1px solid ${BORDER}`,
                borderBottom: isCompact ? `1px solid ${BORDER}` : "none"
              },
              children: Gallery
            }
          ),
          /* @__PURE__ */ jsx("div", { style: { padding: isCompact ? 20 : 32, display: "flex" }, children: Details })
        ]
      }
    )
  ] });
};
export {
  ProductCardWidget
};
//# sourceMappingURL=ProductCardWidget.js.map
